import type { ChatMessage, PersistedChatMessage } from "@/types/chat.type";

/**
 * A Workshop thread is read a page at a time: it opens on its newest
 * THREAD_PAGE messages, and scrolling up reads the page before the oldest one
 * on screen. Back at the newest end, the pages read on the way up are let go
 * again (trimThread), so a long thread costs the same as a short one.
 */
export const THREAD_PAGE = 20;
/** Past this many messages, a viewer at the newest end lets the older ones go. */
export const THREAD_TRIM_AT = THREAD_PAGE + 10;

/** GET /workshop/sessions/:id as a page (see the backend's v_WorkshopSessionPageQuery). */
export type ThreadPageResponse = {
  messages: PersistedChatMessage[];
  /** Older messages exist past this page. */
  hasMore?: boolean;
  /** Every user message in the thread, whatever the page. */
  userRows?: number;
};

/** The newest page, or the page before the message `before`. */
export function threadPageUrl(sessionId: string, before?: string | null): string {
  return `/workshop/sessions/${sessionId}?limit=${THREAD_PAGE}${before ? `&before=${before}` : ""}`;
}

/** Sends this tab shows before the server has them: never a page boundary. */
export const isLocalMessage = (m: Pick<ChatMessage, "id">) => m.id.startsWith("local-");

/** The oldest message the server has: the next older page starts before it. */
export function oldestStored(messages: ChatMessage[]): ChatMessage | null {
  let oldest: ChatMessage | null = null;
  for (const m of messages) {
    if (isLocalMessage(m)) continue;
    if (!oldest || m.createdAt < oldest.createdAt) oldest = m;
  }
  return oldest;
}

/** An older page in front of what is loaded, without repeating a message. */
export function prependOlder(current: ChatMessage[], older: ChatMessage[]): ChatMessage[] {
  const have = new Set(current.map((m) => m.id));
  const fresh = older.filter((m) => !have.has(m.id));
  return fresh.length > 0 ? [...fresh, ...current] : current;
}

/**
 * The newest `keep` messages, in their order; the rest are let go. Null when
 * nothing would go. Sends not stored yet always stay.
 */
export function trimThread(
  messages: ChatMessage[],
  keep = THREAD_PAGE,
): { kept: ChatMessage[]; dropped: ChatMessage[] } | null {
  if (messages.length <= keep) return null;
  const newestFirst = [...messages].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
  const cutoff = newestFirst[keep - 1].createdAt;
  const kept: ChatMessage[] = [];
  const dropped: ChatMessage[] = [];
  for (const m of messages) {
    if (isLocalMessage(m) || m.createdAt >= cutoff) kept.push(m);
    else dropped.push(m);
  }
  return dropped.length > 0 ? { kept, dropped } : null;
}

/**
 * A fresh newest page over what is loaded. Older pages read on the way up stay
 * when the page joins onto them (shares a message with what is loaded); a page
 * that does not (the tab missed more than a page) starts the window over.
 */
export function mergeNewestPage(
  current: ChatMessage[],
  page: ChatMessage[],
  pageHasMore: boolean,
): { messages: ChatMessage[]; keptOlder: boolean } {
  if (!pageHasMore || page.length === 0) return { messages: page, keptOlder: false };
  const pageIds = new Set(page.map((m) => m.id));
  if (!current.some((m) => pageIds.has(m.id))) return { messages: page, keptOlder: false };
  const start = page[0].createdAt;
  const older = current.filter(
    (m) => !isLocalMessage(m) && !pageIds.has(m.id) && m.createdAt < start,
  );
  return { messages: older.length > 0 ? [...older, ...page] : page, keptOlder: older.length > 0 };
}

export const countUserRows = (messages: ChatMessage[]) =>
  messages.reduce((n, m) => n + (m.role === "user" ? 1 : 0), 0);

/**
 * Rows a read brings in for messages already on screen keep those messages'
 * keys (`clientKey`), so nothing is mounted again: matched by id, and a
 * message shown before the server had it (local-…) by role and words.
 */
export function keepKeys(page: ChatMessage[], current: ChatMessage[]): ChatMessage[] {
  const byId = new Map(current.map((m) => [m.id, m]));
  const locals = current.filter(isLocalMessage);
  const used = new Set<string>();
  return page.map((row) => {
    const same = byId.get(row.id);
    if (same) return same.clientKey ? { ...row, clientKey: same.clientKey } : row;
    const local = locals.find(
      (m) => !used.has(m.id) && m.role === row.role && m.content.trim() === row.content.trim(),
    );
    if (!local) return row;
    used.add(local.id);
    return { ...row, clientKey: local.clientKey ?? local.id };
  });
}
